import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Mail, Phone, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Donate = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701153758624-4b3d352bcfbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaGltYWxheWFzJTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBkaGFyYW1zaGFsYXxlbnwwfHx8fDE3NzQ3NjM2OTd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heart className="h-16 w-16 text-white mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
            >
              Support Our Work
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Donation Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* QR Code & Bank Details */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#F9F8F6] rounded-2xl p-8 shadow-sm border border-[#E4E2DC]/40"
            >
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#1C221F] mb-6">
                Donate via UPI
              </h2>
              
              <div className="bg-white p-4 rounded-xl mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/scbnbhit_QR.jpeg"
                  alt="Donation QR Code"
                  className="w-full max-w-sm mx-auto"
                  data-testid="donation-qr-code"
                />
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-[#515E58] mb-1">UPI ID:</p>
                  <p className="text-base font-medium text-[#1C221F]">9817403824@cbin</p>
                </div>
              </div>

              <div className="border-t border-[#E4E2DC] pt-6">
                <h3 className="text-lg font-heading font-medium text-[#1C221F] mb-4">
                  Bank Transfer Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-[#515E58] mb-1">Account Name:</p>
                    <p className="text-sm font-medium text-[#1C221F]">Professional Assistance for Hill Area Development-PAHAD</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#515E58] mb-1">Account Number:</p>
                    <p className="text-sm font-medium text-[#1C221F]">3722389346</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#515E58] mb-1">Bank Name:</p>
                    <p className="text-sm font-medium text-[#1C221F]">Central Bank of India</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#515E58] mb-1">Branch:</p>
                    <p className="text-sm font-medium text-[#1C221F]">Yol, District Kangra, Himachal Pradesh, INDIA</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#515E58] mb-1">IFSC Code:</p>
                    <p className="text-sm font-medium text-[#1C221F]">CBIN0280423</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[#2B5C45]/10 rounded-lg">
                <p className="text-sm text-[#1C221F]">
                  <strong>Note:</strong> Your contribution helps us empower farming communities and preserve traditional knowledge systems in the Himalayan region.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#1C221F] mb-6">
                Get in Touch
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#1C221F] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1C221F] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="contact-email-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#1C221F] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#1C221F] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#2B5C45] text-white hover:bg-[#214835] rounded-full px-8 py-4 font-medium transition-all shadow-sm inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send Message"}
                  <Send className="ml-2 h-5 w-5" />
                </button>
              </form>

              <div className="mt-8 space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-[#2B5C45] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[#1C221F]">Address</p>
                    <p className="text-sm text-[#515E58]">Dharamshala, Himachal Pradesh, India</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-[#2B5C45] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[#1C221F]">Phone</p>
                    <p className="text-sm text-[#515E58]">+91 98174 03824</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-[#2B5C45] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[#1C221F]">Email</p>
                    <p className="text-sm text-[#515E58]">info@pahadtrust.org</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
